import {
  useAllItems,
  type ConversationItem,
} from '@/services/ConversationService';
import { createContext, useContext, useMemo, useReducer } from 'react';
import { Column, Font, ThinBorderStyle } from './DesignSystem';

export type VersionID = string;
export function NewVersionID(): VersionID {
  return crypto.randomUUID();
}

export type Version = {
  id: VersionID;
  name: string;
};

type VersionsPreviewComputedState = {
  versions: Version[];
  unversionedItems: ConversationItem[];
  comparisonItems: ConversationItem[] | undefined;
};

export type VersionsPreviewReducerAction =
  | {
      type: 'select-version';
      versionID: VersionID;
    }
  | {
      type: 'set-mode';
      mode: 'Select' | 'Compare';
    }
  | {
      type: 'select-comparison-version';
      versionID: VersionID;
    };

export type VersionsPreviewReducerDispatch = (
  _: VersionsPreviewReducerAction,
) => void;

export type VersionsPreviewState = {
  activeVersionID?: VersionID;
  mode: 'Select' | 'Compare';
  comparisonIDs: { a: string | null; b: string | null };
  selectingID: 'a' | 'b';
};

export type VersionsPreviewContextProps = {
  state: VersionsPreviewState;
  dispatch: VersionsPreviewReducerDispatch;
};

export type VersionsPreviewExternalContextProps = {
  state: VersionsPreviewComputedState & VersionsPreviewState;
  dispatch: VersionsPreviewReducerDispatch;
};

const VersionsPreviewContext =
  createContext<VersionsPreviewContextProps | null>(null);
export function useVersions(): VersionsPreviewExternalContextProps {
  const context = useContext(VersionsPreviewContext);
  if (context === null) {
    throw new Error('useVersions must be used within a VersionsBoundary');
  }
  const { state, dispatch } = context;
  const allItems = useAllItems({});
  const computedState = useMemo(() => {
    return computeState(allItems, state);
  }, [allItems, state]);

  return {
    state: { ...state, ...computedState },
    dispatch,
  };
}

function computeState(
  items: ConversationItem[],
  state: VersionsPreviewState,
): VersionsPreviewComputedState {
  const isComparing =
    state.comparisonIDs['a'] !== null && state.comparisonIDs['b'] !== null;
  let isBetweenVersions = false;

  return items.reduce<VersionsPreviewComputedState>(
    (acc, e) => {
      switch (e.type) {
        case 'createVersion': {
          const comparisonItems = acc.comparisonItems;
          if (e.id === state.comparisonIDs['a']) {
            isBetweenVersions = true;
          } else if (isBetweenVersions && e.id === state.comparisonIDs['b']) {
            isBetweenVersions = false;
          }

          if (e.id === undefined) {
            console.log('items: ', JSON.stringify(items));
            debugger;
          }

          return {
            versions: acc.versions.concat({ name: e.name, id: e.id }),
            unversionedItems: [],
            comparisonItems,
          };
        }
        case 'defineColor':
        case 'removeColor':
        case 'updateColorPalette':
        case 'message':
        case 'defineTextStyle':
        case 'removeTextStyle':
        case 'updateTextStyles':
        case 'addScreen':
        case 'removeScreen':
        case 'describeScreen':
        case 'connect':
        case 'writeProperties':
        case 'defineComponentStyle':
        case 'removeComponentStyle':
        case 'declareFunctionComponent':
        case 'removeFunctionComponent':
        case 'createComponent':
        case 'addChildComponent':
          // no-op
          let comparisonItems = acc.comparisonItems;
          if (isComparing && isBetweenVersions) {
            if (comparisonItems === undefined) {
              comparisonItems = [e];
            } else {
              comparisonItems = comparisonItems.concat(e);
            }
          }

          return {
            ...acc,
            unversionedItems: acc.unversionedItems.concat(e),
            comparisonItems,
          };
      }
    },
    { versions: [], unversionedItems: [], comparisonItems: undefined },
  );
}

export function VersionsBoundary({ children }: React.PropsWithChildren) {
  function reducer(
    state: VersionsPreviewState,
    action: VersionsPreviewReducerAction,
  ): VersionsPreviewState {
    switch (action.type) {
      case 'select-version': {
        return { ...state, activeVersionID: action.versionID };
      }
      case 'set-mode': {
        return { ...state, mode: action.mode };
      }
      case 'select-comparison-version': {
        const nextSelectingID = state.selectingID === 'a' ? 'b' : 'a';
        const nextComparisonIDs = {
          ...state.comparisonIDs,
          [state.selectingID]: action.versionID,
        };
        return {
          ...state,
          comparisonIDs: nextComparisonIDs,
          selectingID: nextSelectingID,
        };
      }
    }
  }

  const [state, dispatch] = useReducer(reducer, {
    mode: 'Select',
    comparisonIDs: { a: null, b: null },
    selectingID: 'a',
  });

  return (
    <VersionsPreviewContext.Provider
      value={{
        state,
        dispatch,
      }}
    >
      {children}
    </VersionsPreviewContext.Provider>
  );
}

export function VersionPreview({
  isActiveVersion,
  name,
}: {
  isActiveVersion: boolean;
  name: string;
}) {
  return (
    <Column>
      <Font kind="font-body">
        <div
          style={{
            ...ThinBorderStyle,
            padding: 8,
            color: isActiveVersion ? 'green' : 'inherit',
          }}
        >
          {name}
        </div>
      </Font>
    </Column>
  );
}
